<template>
    <BaseModal :visible="visible" :width="viewOnly ? '360px' : '440px'" max-height="85vh"
               content-class="agenda-form-dialog" @close="close">
        <div class="agenda-form-header">
            <span class="agenda-form-title">{{ viewOnly ? $t('pages.module.AGENDA.form.viewEvent') : (isEditing ? $t('pages.module.AGENDA.form.editEvent') : $t('pages.module.AGENDA.form.newEvent')) }}</span>
            <button class="agenda-form-close" @click="close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
        </div>
        <div class="agenda-form-body" v-if="viewOnly">
                <div class="agenda-form-view-title">
                    <div class="agenda-form-view-color" v-if="viewEvent.color" :style="{background: viewEvent.color}"></div>
                    <span>{{ viewEvent.title }}</span>
                </div>
                <div class="agenda-form-view-meta">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    <span>{{ formatViewDate(viewEvent.event_at) }}<template v-if="viewEvent.event_end_at"> — {{ formatViewDate(viewEvent.event_end_at) }}</template></span>
                </div>
                <div class="agenda-form-view-meta" v-if="viewEvent.user">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    <span>{{ viewEvent.user.full_name || viewEvent.user.name || '' }}</span>
                </div>
                <div class="agenda-form-view-desc" v-if="viewEvent.description">{{ viewEvent.description }}</div>
                <div class="agenda-form-view-status">
                    <span class="agenda-sidebar-event-status" :class="'agenda-sidebar-event-status--' + (viewEvent.status?.id === 2 ? 'completed' : 'pending')">
                        {{ viewEvent.status?.id === 2 ? $t('pages.module.AGENDA.status.completed') : $t('pages.module.AGENDA.status.pending') }}
                    </span>
                </div>
            </div>
            <div class="agenda-form-body" v-else>
                <div class="agenda-form-field">
                    <label>{{ $t('pages.module.AGENDA.form.title') }}</label>
                    <input type="text" v-model="form.title" class="agenda-form-input" :class="{'agenda-form-input--error': fieldErrors.title}" :placeholder="$t('pages.module.AGENDA.form.titlePlaceholder')" maxlength="255" @input="clearFieldError('title')" />
                    <div class="agenda-form-field-error" v-if="fieldErrors.title">{{ fieldErrors.title }}</div>
                </div>
                <div class="agenda-form-field">
                    <label>{{ $t('pages.module.AGENDA.form.description') }}</label>
                    <textarea v-model="form.description" class="agenda-form-textarea" :placeholder="$t('pages.module.AGENDA.form.descriptionPlaceholder')" rows="3"></textarea>
                </div>
                <div class="agenda-form-field">
                    <label>{{ $t('pages.module.AGENDA.form.color') }}</label>
                    <div class="agenda-form-colors">
                        <span v-for="c in colorOptions" :key="c.hex"
                              class="agenda-form-color-dot"
                              :class="{'agenda-form-color-dot--active': form.color === c.hex}"
                              :style="{background: c.hex}"
                              :title="c.label"
                              @click="form.color = form.color === c.hex ? null : c.hex"></span>
                    </div>
                </div>
                <div class="agenda-form-row">
                    <div class="agenda-form-field agenda-form-field--half">
                        <label>{{ $t('pages.module.AGENDA.form.eventDate') }}</label>
                        <DateTimePicker
                            v-model="form.event_at"
                            type="datetime"
                            :placeholder="$t('pages.module.AGENDA.form.eventDatePlaceholder')"
                            :error-class="fieldErrors.event_at ? 'dtp--error' : ''"
                            :auto-modal="compact"
                            @change="onEventAtChanged"
                        />
                        <div class="agenda-form-field-error" v-if="fieldErrors.event_at">{{ fieldErrors.event_at }}</div>
                    </div>
                    <div class="agenda-form-field agenda-form-field--half">
                        <label>{{ $t('pages.module.AGENDA.form.eventEndDate') }}</label>
                        <DateTimePicker
                            v-model="form.event_end_at"
                            type="datetime"
                            :placeholder="$t('pages.module.AGENDA.form.eventEndDatePlaceholder')"
                            :disabled="!form.event_at"
                            :min="form.event_at"
                            clearable
                            :error-class="fieldErrors.event_end_at ? 'dtp--error' : ''"
                            :auto-modal="compact"
                            @change="onEventEndAtChanged"
                        />
                        <div class="agenda-form-field-error" v-if="fieldErrors.event_end_at">{{ fieldErrors.event_end_at }}</div>
                    </div>
                </div>

                <div v-if="!isEventInPast" class="agenda-form-field">
                    <label>{{ $t('pages.module.AGENDA.reminder.reminders') }}</label>
                    <div class="agenda-form-reminders">
                        <div v-for="(rem, idx) in form.reminders" :key="idx" class="agenda-form-reminder-item">
                            <div class="agenda-form-segmented">
                                <button class="agenda-form-segmented-btn" :class="{active: rem.type === 'relative'}" @click="rem.type !== 'relative' && (rem.type = 'relative', onReminderTypeChange(rem))">{{ $t('pages.module.AGENDA.reminder.typeBefore') }}</button>
                                <button class="agenda-form-segmented-btn" :class="{active: rem.type === 'absolute'}" @click="rem.type !== 'absolute' && (rem.type = 'absolute', onReminderTypeChange(rem))">{{ $t('pages.module.AGENDA.reminder.customDate') }}</button>
                            </div>
                            <el-select v-if="rem.type === 'relative'" v-model="rem.minutes_before" size="small" popper-class="agenda-app-popper" :teleported="true" style="width:100%">
                                <el-option v-for="m in reminderPresets" :key="m" :value="m" :label="formatReminderLabel(m)" :disabled="isPresetUsed(m, idx)"/>
                            </el-select>
                            <DateTimePicker v-else v-model="rem.remind_at" type="datetime" :placeholder="$t('pages.module.AGENDA.reminder.timePlaceholder')" :min="nowMin" :max="form.event_at" :auto-modal="compact"/>
                            <button class="agenda-form-reminder-remove" @click="removeReminder(idx)">{{ $t('pages.module.AGENDA.reminder.remove') }}</button>
                        </div>
                        <button v-if="form.reminders.length < 5 && !isEventInPast" class="agenda-form-btn agenda-form-btn--add" @click="addReminder">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            {{ $t('pages.module.AGENDA.reminder.addReminder') }}
                        </button>
                    </div>
                </div>

                <div class="agenda-form-field agenda-form-field--recurrence">
                    <div class="agenda-form-recurrence-toggle" @click="toggleRecurrence">
                        <span>{{ $t('pages.module.AGENDA.recurrence.title') }}</span>
                        <span class="agenda-form-switch" :class="{'agenda-form-switch--on': form.recurrence !== null}">
                            <span class="agenda-form-switch-knob"></span>
                        </span>
                    </div>
                    <div v-if="form.recurrence" class="agenda-form-recurrence-fields">
                        <div class="agenda-form-recurrence-freq-row">
                            <span>{{ $t('pages.module.AGENDA.recurrence.every') }}</span>
                            <input type="number" v-model.number="form.recurrence.interval" min="1" max="99" class="agenda-form-input agenda-form-recurrence-interval"/>
                            <div class="agenda-form-segmented agenda-form-recurrence-freq-seg">
                                <button v-for="freq in [{id: 1, label: $t('pages.module.AGENDA.recurrence.daily')}, {id: 2, label: $t('pages.module.AGENDA.recurrence.weekly')}, {id: 3, label: $t('pages.module.AGENDA.recurrence.monthly')}, {id: 4, label: $t('pages.module.AGENDA.recurrence.yearly')}]" :key="freq.id" class="agenda-form-segmented-btn" :class="{active: form.recurrence.frequency_id === freq.id}" @click="form.recurrence.frequency_id = freq.id">{{ freq.label }}</button>
                            </div>
                        </div>
                        <div v-if="recurrenceFreqHint" class="agenda-form-recurrence-hint">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                            <span>{{ recurrenceFreqHint }}</span>
                        </div>
                        <label class="agenda-form-recurrence-end-label">{{ $t('pages.module.AGENDA.recurrence.endType') }}</label>
                        <div class="agenda-form-segmented">
                            <button class="agenda-form-segmented-btn" :class="{active: form.recurrence.end_type_id === 1}" @click="form.recurrence.end_type_id = 1">{{ $t('pages.module.AGENDA.recurrence.indefinite') }}</button>
                            <button class="agenda-form-segmented-btn" :class="{active: form.recurrence.end_type_id === 2}" @click="form.recurrence.end_type_id = 2">{{ $t('pages.module.AGENDA.recurrence.untilDate') }}</button>
                            <button class="agenda-form-segmented-btn" :class="{active: form.recurrence.end_type_id === 3}" @click="form.recurrence.end_type_id = 3; if (!form.recurrence.end_count) form.recurrence.end_count = 1">{{ $t('pages.module.AGENDA.recurrence.afterCount') }}</button>
                        </div>
                        <DateTimePicker v-if="form.recurrence.end_type_id === 2" v-model="form.recurrence.end_at" type="date" :placeholder="$t('pages.module.AGENDA.form.eventDatePlaceholder')" :auto-modal="compact"/>
                        <div v-if="form.recurrence.end_type_id === 3" class="agenda-form-recurrence-count-row">
                            <input type="number" v-model.number="form.recurrence.end_count" min="1" max="999" class="agenda-form-input agenda-form-recurrence-count"/>
                            <span>{{ $t('pages.module.AGENDA.recurrence.times') }}</span>
                        </div>
                        <div v-if="recurrenceEndHint" class="agenda-form-recurrence-hint">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                            <span>{{ recurrenceEndHint }}</span>
                        </div>
                    </div>
                </div>

                <div class="agenda-form-field agenda-form-field--visibility">
                    <label>{{ $t('pages.module.AGENDA.form.visibility') }}</label>
                    <div class="agenda-form-segmented">
                        <button class="agenda-form-segmented-btn" :class="{active: form.visibility_id === 1}" @click="form.visibility_id = 1">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            {{ $t('pages.module.AGENDA.form.visibilityOnlyMe') }}
                        </button>
                        <button class="agenda-form-segmented-btn" :class="{active: form.visibility_id === 2}" @click="form.visibility_id = 2">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                            {{ $t('pages.module.AGENDA.form.visibilityGroup.' + $handler.platformType) }}
                        </button>
                        <button class="agenda-form-segmented-btn" :class="{active: form.visibility_id === 3}" @click="form.visibility_id = 3">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                            {{ $t('pages.module.AGENDA.form.visibilitySelected') }}
                        </button>
                    </div>
                </div>
                <div class="agenda-form-field" v-if="form.visibility_id === 3">
                    <label>{{ $t('pages.module.AGENDA.form.viewers') }}</label>
                    <el-select
                        v-model="form.viewer_ids"
                        :placeholder="$t('pages.module.AGENDA.form.viewersPlaceholder')"
                        multiple
                        filterable
                        size="small"
                        popper-class="agenda-app-popper"
                        :teleported="true"
                        style="width:100%"
                        @change="clearFieldError('viewers')">
                        <el-option v-for="o in officerList" :key="o.id" :label="o.name" :value="o.id"/>
                    </el-select>
                    <div class="agenda-form-field-error" v-if="fieldErrors.viewers">{{ fieldErrors.viewers }}</div>
                </div>

                <div class="agenda-form-error" v-if="serverError">{{ serverError }}</div>
            </div>
        <div class="agenda-form-footer" v-if="!viewOnly">
            <button class="agenda-form-btn agenda-form-btn--cancel" @click="close">{{ $t('pages.module.AGENDA.form.cancel') }}</button>
            <button class="agenda-form-btn agenda-form-btn--save" @click="submit" :disabled="saving">
                {{ saving ? $t('pages.module.AGENDA.form.saving') : $t('pages.module.AGENDA.form.save') }}
            </button>
        </div>
    </BaseModal>
</template>

<script>
import DateTimePicker from '@/components/common/date-time-picker/DateTimePicker.vue'
import BaseModal from '@/components/common/base-modal/BaseModal.vue'

const EVENT_COLORS_KEYS = ['red', 'orange', 'yellow', 'green', 'blue', 'purple', 'pink', 'gray']
const EVENT_COLORS_HEX = ['#FF3B30', '#FF9500', '#FFCC00', '#34C759', '#007AFF', '#AF52DE', '#FF2D55', '#8E8E93']

export default {
    name: 'AgendaEventForm',
    components: { DateTimePicker, BaseModal },
    props: {
        officers: {type: Array, default: () => []},
        compact: {type: Boolean, default: false},
    },
    data() {
        return {
            visible: false,
            saving: false,
            viewOnly: false,
            viewEvent: null,
            editingEvent: null,
            serverError: '',
            fieldErrors: {},
            form: this.getEmptyForm(),
            nowMin: null,
        }
    },
    computed: {
        isEditing() {
            return !!this.editingEvent
        },
        isEventInPast() {
            if (!this.form.event_at) return false
            const ref = this.form.event_end_at || this.form.event_at
            return new Date(ref.replace(' ', 'T')) < new Date()
        },
        recurrenceFreqHint() {
            if (!this.form.recurrence) return ''
            const freqMap = {1: this.$t('pages.module.AGENDA.recurrence.daily').toLowerCase(), 2: this.$t('pages.module.AGENDA.recurrence.weekly').toLowerCase(), 3: this.$t('pages.module.AGENDA.recurrence.monthly').toLowerCase(), 4: this.$t('pages.module.AGENDA.recurrence.yearly').toLowerCase()}
            const freq = freqMap[this.form.recurrence.frequency_id] || ''
            const n = this.form.recurrence.interval || 1
            if (n === 1) return this.$t('pages.module.AGENDA.recurrence.repeatEvery', {freq})
            return this.$t('pages.module.AGENDA.recurrence.repeatEveryN', {n, freq})
        },
        recurrenceEndHint() {
            if (!this.form.recurrence) return ''
            if (this.form.recurrence.end_type_id === 1) return this.$t('pages.module.AGENDA.recurrence.endNever')
            if (this.form.recurrence.end_type_id === 2 && this.form.recurrence.end_at) return this.$t('pages.module.AGENDA.recurrence.endOnDate', {date: this.formatViewDate(this.form.recurrence.end_at)})
            if (this.form.recurrence.end_type_id === 3 && this.form.recurrence.end_count) {
                const freqMap = {1: this.$t('pages.module.AGENDA.recurrence.daily').toLowerCase(), 2: this.$t('pages.module.AGENDA.recurrence.weekly').toLowerCase(), 3: this.$t('pages.module.AGENDA.recurrence.monthly').toLowerCase(), 4: this.$t('pages.module.AGENDA.recurrence.yearly').toLowerCase()}
                const freq = freqMap[this.form.recurrence.frequency_id] || ''
                return this.$t('pages.module.AGENDA.recurrence.endAfterN', {n: this.form.recurrence.end_count, freq})
            }
            return ''
        },
        colorOptions() {
            return EVENT_COLORS_HEX.map((hex, i) => ({
                hex,
                label: this.$t('pages.module.AGENDA.colors.' + EVENT_COLORS_KEYS[i]),
            }))
        },
        currentUserId() {
            return this.$store.state.AuthModule?.user?.id || null
        },
        officerList() {
            return this.officers.map(o => this.$handler.mapMember(o)).filter(o => o.id && o.name && o.id !== this.currentUserId)
        },
        reminderPresets() {
            return [5, 10, 15, 30, 60, 120, 1440, 2880]
        },
    },
    methods: {
        formatReminderLabel(minutes) {
            if (minutes < 60) return this.$t('pages.module.AGENDA.reminder.minutesBefore', {n: minutes})
            if (minutes < 1440) return this.$t('pages.module.AGENDA.reminder.hoursBefore', {n: minutes / 60})
            return this.$t('pages.module.AGENDA.reminder.daysBefore', {n: minutes / 1440})
        },
        isPresetUsed(minutes, currentIdx) {
            return this.form.reminders.some((r, i) => i !== currentIdx && r.type === 'relative' && r.minutes_before === minutes)
        },
        addReminder() {
            if (this.form.reminders.length >= 5) return
            const used = new Set(this.form.reminders.filter(r => r.type === 'relative').map(r => r.minutes_before))
            const first = this.reminderPresets.find(m => !used.has(m)) || this.reminderPresets[0]
            this.form.reminders.push({type: 'relative', minutes_before: first, remind_at: null})
        },
        removeReminder(idx) {
            this.form.reminders.splice(idx, 1)
        },
        onReminderTypeChange(rem) {
            if (rem.type === 'relative') {
                const used = new Set(this.form.reminders.filter(r => r !== rem && r.type === 'relative').map(r => r.minutes_before))
                rem.minutes_before = this.reminderPresets.find(m => !used.has(m)) || this.reminderPresets[0]
                rem.remind_at = null
            } else {
                rem.minutes_before = null
                rem.remind_at = null
            }
        },
        toggleRecurrence() {
            if (this.form.recurrence) {
                this.form.recurrence = null
            } else {
                this.form.recurrence = {frequency_id: 3, interval: 1, end_type_id: 1, end_at: null, end_count: null}
            }
        },
        onEventEndAtChanged() {
            this.clearFieldError('event_end_at')
            if (!this.form.event_end_at || !this.form.event_at) return
            const end = new Date(this.form.event_end_at.replace(' ', 'T'))
            const start = new Date(this.form.event_at.replace(' ', 'T'))
            if (end < start) {
                this.fieldErrors = {...this.fieldErrors, event_end_at: this.$t('pages.module.AGENDA.form.endMustBeAfterStart')}
            }
        },
        onEventAtChanged() {
            this.clearFieldError('event_at')
            if (!this.form.event_at) return
            if (this.form.event_end_at) {
                const end = new Date(this.form.event_end_at.replace(' ', 'T'))
                const start = new Date(this.form.event_at.replace(' ', 'T'))
                if (end < start) {
                    this.form.event_end_at = null
                    this.clearFieldError('event_end_at')
                }
            }
        },
        getEmptyForm() {
            return {
                title: '',
                description: '',
                color: null,
                event_at: null,
                event_end_at: null,
                reminders: [],
                recurrence: null,
                visibility_id: 1,
                viewer_ids: [],
            }
        },
        clearFieldError(field) {
            if (this.fieldErrors[field]) {
                const copy = {...this.fieldErrors}
                delete copy[field]
                this.fieldErrors = copy
            }
        },
        disablePastDate(date) {
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            return date.getTime() < today.getTime()
        },
        formatViewDate(dateStr) {
            if (!dateStr) return ''
            const d = new Date(dateStr.replace(' ', 'T'))
            if (isNaN(d.getTime())) return dateStr
            const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
            const day = d.getDate()
            const month = months[d.getMonth()]
            const year = d.getFullYear()
            const hour = String(d.getHours()).padStart(2, '0')
            const min = String(d.getMinutes()).padStart(2, '0')
            return `${day} ${month} ${year}, ${hour}:${min}`
        },
        _startNowTimer() {
            this._clearNowTimer()
            this.nowMin = new Date()
            const msToNextMin = (60 - new Date().getSeconds()) * 1000
            this._nowTimeout = setTimeout(() => {
                this.nowMin = new Date()
                this._nowInterval = setInterval(() => { this.nowMin = new Date() }, 60000)
            }, msToNextMin)
        },
        _clearNowTimer() {
            clearTimeout(this._nowTimeout)
            clearInterval(this._nowInterval)
        },
        openView(event) {
            this.viewOnly = true
            this.viewEvent = event
            this.editingEvent = null
            this.serverError = ''
            this.fieldErrors = {}
            this.visible = true
        },
        openNew(presetDate) {
            this.editingEvent = null
            this.serverError = ''
            this.fieldErrors = {}
            this.form = this.getEmptyForm()
            if (presetDate) {
                const hasTime = presetDate.includes('T') || presetDate.includes(' ')
                this.form.event_at = hasTime ? presetDate.replace('T', ' ') : presetDate + ' 09:00'
            }
            this.visible = true
            this._startNowTimer()
        },
        openEdit(event) {
            this.editingEvent = event
            this.serverError = ''
            this.fieldErrors = {}
            this.form = {
                title: event.title || '',
                description: event.description || '',
                color: event.color || null,
                event_at: event.event_at ? event.event_at.substring(0, 16).replace('T', ' ') : null,
                event_end_at: event.event_end_at ? event.event_end_at.substring(0, 16).replace('T', ' ') : null,
                reminders: (event.reminders || []).map(r => ({
                    type: r.type || (r.minutes_before != null ? 'relative' : 'absolute'),
                    minutes_before: r.minutes_before ?? null,
                    remind_at: r.remind_at ? r.remind_at.substring(0, 16).replace('T', ' ') : null,
                })),
                recurrence: event.recurrence ? {
                    frequency_id: event.recurrence.frequency?.id || event.recurrence.frequency_id || 3,
                    interval: event.recurrence.interval || 1,
                    end_type_id: event.recurrence.end_type?.id || event.recurrence.end_type_id || 1,
                    end_at: event.recurrence.end_at ? event.recurrence.end_at.substring(0, 10) : null,
                    end_count: event.recurrence.end_count || null,
                } : null,
                visibility_id: event.visibility?.id || 1,
                viewer_ids: (event.viewers || []).map(v => v.id),
            }
            this.visible = true
            this._startNowTimer()
        },
        close() {
            this._clearNowTimer()
            this.visible = false
            this.viewOnly = false
            this.viewEvent = null
            this.editingEvent = null
            this.serverError = ''
            this.fieldErrors = {}
            this.saving = false
            this.nowMin = null
        },
        validate() {
            const errors = {}
            if (!this.form.title.trim()) {
                errors.title = this.$t('pages.module.AGENDA.form.titleRequired')
            }
            if (!this.form.event_at) {
                errors.event_at = this.$t('pages.module.AGENDA.form.dateRequired')
            }
            if (this.form.event_end_at && this.form.event_at) {
                const end = new Date(this.form.event_end_at.replace(' ', 'T'))
                const start = new Date(this.form.event_at.replace(' ', 'T'))
                if (end < start) {
                    errors.event_end_at = this.$t('pages.module.AGENDA.form.endMustBeAfterStart')
                }
            }
            if (this.form.visibility_id === 3 && this.form.viewer_ids.length === 0) {
                errors.viewers = this.$t('pages.module.AGENDA.form.viewerRequired')
            }
            this.fieldErrors = errors
            return Object.keys(errors).length === 0
        },
        _hasFormChanges() {
            if (!this.editingEvent) return true
            const e = this.editingEvent
            const f = this.form
            if (f.title.trim() !== (e.title || '')) return true
            if ((f.description?.trim() || '') !== (e.description || '')) return true
            if ((f.color || null) !== (e.color || null)) return true
            const origAt = e.event_at ? e.event_at.substring(0, 16).replace('T', ' ') : null
            if (f.event_at !== origAt) return true
            const origEndAt = e.event_end_at ? e.event_end_at.substring(0, 16).replace('T', ' ') : null
            if ((f.event_end_at || null) !== (origEndAt || null)) return true
            const origVis = e.visibility?.id || 1
            if (f.visibility_id !== origVis) return true
            const origViewers = (e.viewers || []).map(v => v.id).sort().join(',')
            if (f.viewer_ids.slice().sort().join(',') !== origViewers) return true
            const origReminders = (e.reminders || []).map(r => JSON.stringify({type: r.type || (r.minutes_before != null ? 'relative' : 'absolute'), minutes_before: r.minutes_before ?? null, remind_at: r.remind_at ? r.remind_at.substring(0, 16).replace('T', ' ') : null})).sort().join('|')
            const formReminders = f.reminders.map(r => JSON.stringify({type: r.type, minutes_before: r.minutes_before ?? null, remind_at: r.remind_at})).sort().join('|')
            if (formReminders !== origReminders) return true
            const origRec = e.recurrence ? JSON.stringify({frequency_id: e.recurrence.frequency?.id || e.recurrence.frequency_id, interval: e.recurrence.interval, end_type_id: e.recurrence.end_type?.id || e.recurrence.end_type_id, end_at: e.recurrence.end_at ? e.recurrence.end_at.substring(0, 10) : null, end_count: e.recurrence.end_count || null}) : null
            const formRec = f.recurrence ? JSON.stringify({frequency_id: f.recurrence.frequency_id, interval: f.recurrence.interval, end_type_id: f.recurrence.end_type_id, end_at: f.recurrence.end_type_id === 2 ? f.recurrence.end_at : null, end_count: f.recurrence.end_type_id === 3 ? f.recurrence.end_count : null}) : null
            if (formRec !== origRec) return true
            return false
        },
        async submit() {
            if (!this.validate()) return

            const isRecurring = this.isEditing && this.editingEvent.recurrence
            if (isRecurring && this._hasFormChanges()) {
                const scope = await this.askUpdateScope()
                if (!scope) return
                this._pendingUpdateScope = scope
                if (scope !== 'this_only') {
                    const origStartDay = (this.editingEvent.event_at || '').substring(0, 10)
                    const newStartDay = (this.form.event_at || '').substring(0, 10)
                    const origEndDay = (this.editingEvent.event_end_at || '').substring(0, 10)
                    const newEndDay = (this.form.event_end_at || '').substring(0, 10)
                    if (origStartDay !== newStartDay || origEndDay !== newEndDay) {
                        const realign = await this.askRealign()
                        if (realign === null) return
                        if (realign) this._pendingRealign = true
                    }
                }
            }

            this.saving = true
            const formData = {
                title: this.form.title.trim(),
                description: this.form.description?.trim() || null,
                color: this.form.color || null,
                event_at: this.form.event_at,
                event_end_at: this.form.event_end_at || null,
                reminders: this.form.reminders.map(r => {
                    if (r.type === 'relative') return {minutes_before: r.minutes_before}
                    return {remind_at: r.remind_at}
                }),
                recurrence: this.form.recurrence ? {
                    frequency_id: this.form.recurrence.frequency_id,
                    interval: this.form.recurrence.interval,
                    end_type_id: this.form.recurrence.end_type_id,
                    end_at: this.form.recurrence.end_type_id === 2 ? this.form.recurrence.end_at : null,
                    end_count: this.form.recurrence.end_type_id === 3 ? this.form.recurrence.end_count : null,
                } : null,
                status_id: this.isEditing ? (this.editingEvent.status?.id || 1) : 1,
                visibility_id: this.form.visibility_id || null,
                ...this.$handler.getContextParams(),
                viewer_ids: this.form.visibility_id === 3 ? this.form.viewer_ids : [],
            }
            if (this._pendingUpdateScope) {
                formData.update_scope = this._pendingUpdateScope
                this._pendingUpdateScope = null
            }
            if (this._pendingRealign) {
                formData.realign = true
                this._pendingRealign = false
            }
            if (this.isEditing) {
                this.$emit('save', {id: this.editingEvent.id, formData})
            } else {
                this.$emit('save', {id: null, formData})
            }
        },
        askUpdateScope() {
            const rec = this.editingEvent.recurrence
            if (rec.occurrence_count <= 1) return Promise.resolve('this_only')
            return new Promise(resolve => {
                const buttons = [
                    {label: this.$t('pages.module.AGENDA.scope.thisOnly'), type: 'primary', value: 'this_only'},
                ]
                if (rec.has_future && rec.has_past) buttons.push({label: this.$t('pages.module.AGENDA.scope.thisAndFuture'), type: 'default', value: 'this_and_future'})
                buttons.push(
                    {label: this.$t('pages.module.AGENDA.scope.all'), type: 'danger', value: 'all'},
                    {label: this.$t('common.cancel'), type: 'cancel', value: false},
                )
                this.eventBus.$emit('showConfirmDialog', {
                    title: this.$t('pages.module.AGENDA.scope.updateTitle'),
                    message: this.$t('pages.module.AGENDA.scope.updateMessage'),
                    buttons,
                    onConfirm: (val) => resolve(val),
                    onCancel: () => resolve(null),
                })
            })
        },
        askRealign() {
            return new Promise(resolve => {
                this.$nextTick(() => this.eventBus.$emit('showConfirmDialog', {
                    title: this.$t('pages.module.AGENDA.scope.realignTitle'),
                    message: this.$t('pages.module.AGENDA.scope.realignMessage'),
                    buttons: [
                        {label: this.$t('pages.module.AGENDA.scope.realignShift'), type: 'primary', value: false},
                        {label: this.$t('pages.module.AGENDA.scope.realignAlign'), type: 'danger', value: true},
                        {label: this.$t('common.cancel'), type: 'cancel', value: null},
                    ],
                    onConfirm: (val) => resolve(val),
                    onCancel: () => resolve(null),
                }))
            })
        },
        onSaveComplete() {
            this.saving = false
            this.close()
        },
        onSaveError(msg) {
            this.saving = false
            this.serverError = msg || this.$t('pages.module.AGENDA.form.saveError')
        },
    },
    beforeUnmount() {
        this._clearNowTimer()
    },
}
</script>
